import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ROADMAP_INTRODUCTION_SPOTLIGHT_DISMISSED,
	ROADMAP_INTRODUCTION_SPOTLIGHT_PREREQUISITE_DISMISSALS,
} from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';
import { checkPrerequisiteDismissals } from '../../../../common/utils.tsx';

export const loadRoadmapIntroductionDismissed =
	(): Action<State, Props, Promise<boolean | undefined>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		if (fg('polaris_premium_modal_ga')) {
			await maybeWaitForPremiumModal(getState);
		}

		// User property is already loaded
		if (typeof getState().collections.introductionDismissed === 'boolean') {
			return;
		}

		try {
			const introductionDismissed = await getUserProperty<boolean>(
				accountId,
				ROADMAP_INTRODUCTION_SPOTLIGHT_DISMISSED,
			);

			const state = getState();

			setState({
				...state,
				collections: {
					...state.collections,
					introductionDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					collections: {
						...state.collections,
						introductionDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmaps.introduction.spotlight.dismissed',
				`Failed to get user property with key: ${ROADMAP_INTRODUCTION_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
		return getState().collections.introductionDismissed;
	};

export const loadRoadmapIntroductionPrerequisiteDismissalsMet =
	(): Action<State, Props, Promise<boolean | undefined>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		let dismissals: boolean[] | undefined = [];
		try {
			dismissals = await checkPrerequisiteDismissals(
				ROADMAP_INTRODUCTION_SPOTLIGHT_PREREQUISITE_DISMISSALS,
				accountId,
			);
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmaps.introduction.spotlight.prerequisites',
				'Failed to get user properties for roadmap introduction spotlight prerequisites',
				error instanceof Error ? error : undefined,
			);
		}

		const state = getState();
		setState({
			...state,
			collections: {
				...state.collections,
				introductionPrerequisiteDismissalsMet:
					dismissals?.every((dismissal: boolean) => dismissal) ?? false,
			},
		});

		return getState().collections.introductionPrerequisiteDismissalsMet;
	};

export const setRoadmapIntroductionDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			collections: {
				...state.collections,
				introductionDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, ROADMAP_INTRODUCTION_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmaps.introduction.spotlight.dismissed',
				`Failed to set user property with key: ${ROADMAP_INTRODUCTION_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};
